import styles from './index.css';

// Please replace, if IE11-support isn't required any longer.
//import {useFormContext} from 'react-hook-form';
import {useFormContext} from 'react-hook-form/dist/react-hook-form.ie11';

import * as React from 'react';
import DatePicker, {registerLocale} from 'react-datepicker';
import {range}from 'lodash';
import classNames from 'classnames';
import {format, parse} from 'date-fns';
import locale from 'date-fns/locale/de';

import Icon from '../Icon';
import TextInput from '../TextInput';
import Selectbox from '../Selectbox/ControlledSelectbox';
import {useHandlerOnOuterClick} from '../../Utils/Hooks';

registerLocale('de', locale);

type DateInputProps = {
	name: string;
	required?: boolean;
	minDate?: string;
	maxDate?: string;
	dateFormat?: string;
}
export default (props: DateInputProps) => {
	const {
		name,
		required = false,
		minDate,
		maxDate,
		dateFormat = 'dd.MM.yyyy',
	} = props;

	if (!name) {
		console.warn('Missing prop "name" for DateInput.');
		return null;
	}

	const {setValue, getValues} = useFormContext();

	// Toggle-state.
	const [isOpen, setIsOpen] = React.useState(false);
	const toggle = () => setIsOpen(!isOpen);
	const close = () => setIsOpen(false);
	const {node, enableHandler, disableHandler} = useHandlerOnOuterClick(close, false);

	React.useEffect(() => {
		if (isOpen) {
			enableHandler();
		} else {
			disableHandler();
		}
	}, [isOpen]);

	// Data.
	const update = (date: Date) => {
		setValue(name, format(date, dateFormat), true);
		close();
	};
	const parseDateString = (dateString: string|undefined) => dateString
		? parse(dateString, dateFormat, new Date(), {locale})
		: undefined;
	const selected = parseDateString(getValues()[name]);

	return (
		<div
			ref={node}
			className={classNames(
				styles.dateinput,
				isOpen && styles.isOpen,
			)}
		>
			<div
				onClick={toggle}
				className={styles.toggler}
			>
				<TextInput
					name={name}
					required={required}
					readOnly
					deletable={false}
					hasPlaceholderForIcon={true}
				/>
				<Icon name="calendar" className={styles.icon}/>
			</div>

			<div className={styles.datepickerContainer}>
				<DatePicker
					locale="de"
					{...{selected, minDate, maxDate}}
					onChange={update}
					inline
					showMonthDropdown
					showYearDropdown
					calendarClassName={styles.calendar}
					dayClassName={() => styles.day}
					renderCustomHeader={(props: any) => (
						<DatePickerHeader
							{...props}
							openToDate={selected || minDate || new Date()}
							minDate={parseDateString(minDate)}
						/>
					)}
					renderDayContents={(day: number) => day}
				/>
			</div>
		</div>
	);
}

const toOption = (value: string|number) => ({value: value});

const toOptionWithIndex = (value: string|number, index: number) => ({
	value: index,
	label: value.toString(),
});

type MonthYear = {
	month: number;
	year: number;
}

const prevMonth = ({month, year}: MonthYear): MonthYear => ({
	month: (month + 12 - 1) % 12,
	year: month === 0 ? (year - 1) : year
});

const nextMonth = ({month, year}: MonthYear): MonthYear => ({
	month: (month + 1) % 12,
	year: month === 11 ? (year + 1) : year
});

const initValidation = (min: MonthYear, max: MonthYear) => ({month, year}: MonthYear) => {
	const tooLate = (year === min.year && month < min.month) || (year < min.year);
	const tooEarly = (year === max.year && month > max.month) || (year > max.year);

	return !(tooLate || tooEarly);
};

type HeaderProps = {
	changeMonth: (value: number) => void;
	changeYear: (value: number) => void;
	openToDate: Date;
	minDate: Date;
}
const DatePickerHeader = React.memo(({changeYear, changeMonth, openToDate, minDate}: HeaderProps) => {
	const currentYear = (new Date()).getFullYear();
	const years = range(currentYear, currentYear + 10);
	const months = range(12).map(locale.localize!.month);

	const nowYear = openToDate.getFullYear();
	const nowMonth = openToDate.getMonth();

	const [current, setCurrent] = React.useState({month: nowMonth, year: nowYear});
	const isValid = initValidation({month: minDate.getMonth(), year: minDate.getFullYear()}, {month: 11, year: currentYear + 10});
	const update = (update: {} = {}) => setCurrent((state: MonthYear) => {
		const nextState = {
			...state,
			...update
		} as MonthYear;

		return isValid(nextState)
			? nextState
			: state;
	});

	React.useEffect(() => changeMonth(current.month), [current.month, changeMonth]);
	React.useEffect(() => changeYear(current.year), [current.year, changeYear]);

	React.useEffect(() => {
		setCurrent({month: nowMonth, year: nowYear});
	}, [openToDate]);

	return (
		<div className={styles.header}>
			<Icon
				name="chevron-left"
				onClick={() => update(prevMonth(current))}
				className={classNames(!isValid(prevMonth(current)) && styles.disabled)}
			/>
			<Selectbox
				options={months.map(toOptionWithIndex)}
				className={styles.select}
				displayTiny
				onSelect={(month: number) => update({month})}
				value={current.month}
			/>
			<Selectbox
				options={years.map(toOption)}
				className={styles.select}
				displayTiny
				onSelect={(year: number) => update({year})}
				value={current.year}
			/>
			<Icon
				name="chevron-right"
				onClick={() => update(nextMonth(current))}
				className={classNames(!isValid(nextMonth(current)) && styles.disabled)}
			/>
		</div>
	);
});
